import React, { useState, useEffect, useRef } from 'react';
import { useSocket, CONN_STATE } from '../context/SocketContext';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { useCall } from '../context/CallContext';
import axios from 'axios';
const _api = axios.create({baseURL:'/api'});
_api.interceptors.request.use(function(r){var t=localStorage.getItem('token');if(t)r.headers.Authorization='Bearer '+t;return r;});

const FLAGS = { en:'🇺🇸',zh:'🇨🇳',ja:'🇯🇵',ko:'🇰🇷',es:'🇪🇸',fr:'🇫🇷',it:'🇮🇹',ru:'🇷🇺',de:'🇩🇪',pt:'🇵🇹' };
const LANG_NAMES = { en:'English',zh:'中文',ja:'日本語',ko:'한국어',es:'Español',fr:'Français',it:'Italiano',ru:'Русский' };
const LANG_LIST = [
  { code:'zh', label:'中文 🀄' }, { code:'en', label:'English 🗽' },
  { code:'ja', label:'日本語 🗾' }, { code:'ko', label:'한국어 🌸' },
  { code:'es', label:'Español 💃' }, { code:'fr', label:'Français 🥐' },
  { code:'it', label:'Italiano 🍝' }, { code:'ru', label:'Русский 🎪' },
];
const CONN_BANNER = {
  [CONN_STATE.CONNECTED]: null,
  [CONN_STATE.DISCONNECTED]: { text:'🔌 连接断开', cls:'banner-offline' },
  [CONN_STATE.RECONNECTING]: { text:'🔄 重连中...', cls:'banner-reconnect' },
};

export default function ChatBox({ session, partner, onClose }) {
  const [messages, setMessages] = useState([]);
  const [revealedImages, setRevealedImages] = useState({});
  const [previewImage, setPreviewImage] = useState(null);
  const [cameraOpen, setCameraOpen] = useState(false);
  const [cameraStream, setCameraStream] = useState(null);
  const [input, setInput] = useState('');
  const [targetLang, setTargetLang] = useState(partner?.preferred_language || 'en');
  const [partnerTyping, setPartnerTyping] = useState(false);
  const [membership, setMembership] = useState(null);
  const [quotaMsg, setQuotaMsg] = useState('');
  const [retention, setRetention] = useState({ownMode:'standard',effectiveMode:'standard'});
  const [retentionSaving, setRetentionSaving] = useState(false);
  const [openingMove, setOpeningMove] = useState('');
  const [deleteMenu, setDeleteMenu] = useState(null); // { msgId, x, y }
  const call = useCall();
  const longPressTimer = useRef(null);
  const messagesEndRef = useRef(null); const inputRef = useRef(null); const typingTimer = useRef(null);
  const { socket, connState, safeEmit } = useSocket();
  const { user } = useAuth();
  const navigate = useNavigate();
  const partnerFlag = FLAGS[partner?.preferred_language] || '🌍';
  const [sourceLang, setSourceLang] = useState(user?.preferred_language || 'en');
  const connBanner = CONN_BANNER[connState];
  const isFree = membership?.tier === 'free';
  const translationLeft = membership?.quota?.remaining;
  const showUpgrade = isFree && translationLeft !== undefined && translationLeft <= 5;

  useEffect(() => { messagesEndRef.current?.scrollIntoView({ behavior:'smooth' }); }, [messages]);
  useEffect(() => { setTimeout(() => inputRef.current?.focus(), 200); }, []);
  useEffect(() => { if (cameraOpen && cameraStream && cameraVideoRef.current) { cameraVideoRef.current.srcObject = cameraStream; cameraVideoRef.current.play().catch(() => {}); } }, [cameraOpen, cameraStream]);
  useEffect(() => () => { if (cameraStream) cameraStream.getTracks().forEach(track => track.stop()); }, [cameraStream]);
  useEffect(() => { const partnerId=partner?.user_id||partner?.id; if(!partnerId)return; _api.get('/profile/'+partnerId).then(r=>setOpeningMove(r.data.opening_move||'')).catch(()=>setOpeningMove('')); }, [partner?.user_id, partner?.id]);

  // 获取会员状态
  useEffect(() => {
    axios.get('/api/membership/status').then(r => setMembership(r.data)).catch(() => {});
  }, []);

  // 历史消息
  useEffect(() => {
    if (session.id) _api.get('/messages/' + session.id).then(r => setMessages(r.data)).catch(() => {});
  }, [session.id]);

  useEffect(() => {
    if (!session.id) return;
    _api.get('/chat-retention?sessionId=' + encodeURIComponent(session.id)).then(r => setRetention(r.data)).catch(() => {});
  }, [session.id]);

  // Socket 监听
  useEffect(() => {
    if (!socket) return;
    const onMsg = (data) => setMessages(prev => {
      if (prev.find(m => m.id === data.message.id)) return prev;
      const ti = prev.findIndex(m => m.id.startsWith('local-') && m.sender_id === data.message.sender_id && m.original_text === data.message.original_text);
      if (ti !== -1) { const n = [...prev]; n[ti] = data.message; return n; }
      return [...prev, data.message];
    });
    const onTyping = () => { setPartnerTyping(true); clearTimeout(typingTimer.current); typingTimer.current = setTimeout(() => setPartnerTyping(false), 2000); };
    const onQuota = (data) => { setQuotaMsg(data.message); setTimeout(() => setQuotaMsg(''), 5000); };
    const onDeleted = (data) => {
      setMessages(prev => prev.map(m => {
        if (m.id === data.messageId) {
          if (data.mode === 'both') return { ...m, deleted_for: 'both' };
          return { ...m, deleted_for_sender: true };
        }
        return m;
      }));
    };
    socket.on('new_message', onMsg);
    socket.on('partner_typing', onTyping);
    socket.on('quota_exceeded', onQuota);
    socket.on('message_deleted', onDeleted);
    return () => {
      socket.off('new_message', onMsg); socket.off('partner_typing', onTyping);
      socket.off('quota_exceeded', onQuota); socket.off('message_deleted', onDeleted);
    };
  }, [socket, user.id]);

  useEffect(() => {
    if (!socket || !session.id) return;
    const unreadIds = messages.filter(m => m.sender_id !== user.id && !m.is_read && !String(m.id).startsWith('local-')).map(m => m.id);
    if (unreadIds.length) socket.emit('mark_read', {sessionId:session.id, messageIds:unreadIds});
  }, [messages, socket, session.id, user.id]);

  const sendMessage = () => {
    if (!input.trim() || !session.id) return;
    const text = input.trim();
    const tempId = 'local-' + Date.now();
    const isOffline = connState !== CONN_STATE.CONNECTED;
    setMessages(prev => [...prev, { id:tempId, session_id:session.id, sender_id:user.id,
      original_text:text, translated_text:text + (isOffline ? ' ⏳' : ''),
      source_language:sourceLang, target_language:targetLang,
      created_at:new Date().toISOString(), _pending:isOffline }]);
    safeEmit('send_message', { sessionId:session.id, message:text, targetLanguage:targetLang, sourceLanguage:sourceLang });
    setInput(''); inputRef.current?.focus();
  };
  
  const sendImage = async (file) => {
    if (!session.id) return;
    const fd = new FormData();
    fd.append('file', file);
    try {
      const res = await _api.post('/upload', fd);
      const imgUrl = res.data.url;
      const tempId = 'local-img-' + Date.now();
      setMessages(prev => [...prev, { id:tempId, session_id:session.id, sender_id:user.id,
        type:'image', image_url:imgUrl, original_text:'', translated_text:'',
        source_language:sourceLang, target_language:targetLang,
        created_at:new Date().toISOString() }]);
      safeEmit('send_message', { sessionId:session.id, message:'', targetLanguage:targetLang,
        sourceLanguage:sourceLang, type:'image', image_url:imgUrl });
    } catch(e) { console.error('Upload failed', e); }
  };
  
  const fileInputRef = useRef(null);
  const cameraVideoRef = useRef(null);
  const handleImagePick = () => { fileInputRef.current?.click(); };
  const closeCamera = () => {
    if (cameraStream) cameraStream.getTracks().forEach(track => track.stop());
    setCameraStream(null); setCameraOpen(false);
  };
  const handleCameraCapture = async () => {
    if (!navigator.mediaDevices?.getUserMedia) { setQuotaMsg('当前设备不支持网页相机，请使用相册'); return; }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({video:{facingMode:{ideal:'environment'}},audio:false});
      setCameraStream(stream); setCameraOpen(true);
    } catch (e) { setQuotaMsg('无法打开相机，请允许相机权限后重试'); setTimeout(() => setQuotaMsg(''), 5000); }
  };
  const takeCameraPhoto = () => {
    const video = cameraVideoRef.current; if (!video || !video.videoWidth) return;
    const maxSide = 1920, scale = Math.min(1, maxSide / Math.max(video.videoWidth, video.videoHeight));
    const canvas = document.createElement('canvas'); canvas.width = Math.round(video.videoWidth * scale); canvas.height = Math.round(video.videoHeight * scale);
    canvas.getContext('2d').drawImage(video, 0, 0, canvas.width, canvas.height);
    canvas.toBlob(blob => { if (!blob) return; const file = new File([blob], 'camera-' + Date.now() + '.jpg', {type:'image/jpeg'}); closeCamera(); sendImage(file); }, 'image/jpeg', 0.88);
  };

  const toggleRetention = async () => {
    if (retentionSaving) return;
    const next = retention.ownMode === 'none' ? 'standard' : 'none';
    if (next === 'none' && !window.confirm('开启后，新消息仅保留到当天结束，并在次日北京时间00:00删除。继续吗？')) return;
    setRetentionSaving(true);
    try {
      await _api.put('/chat-retention', {mode:next});
      const res = await _api.get('/chat-retention?sessionId=' + encodeURIComponent(session.id));
      setRetention(res.data);
    } finally { setRetentionSaving(false); }
  };

  const handleKeyDown = (e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendMessage(); } };



  const handleInputChange = (e) => { setInput(e.target.value); if (connState===CONN_STATE.CONNECTED && socket && session.id) socket.emit('typing', { sessionId:session.id }); };
  
  const handleDelete = (msgId, mode) => {
    if (socket && socket.connected) {
      socket.emit('delete_message', { messageId: msgId, mode });
    }
    setDeleteMenu(null);
  };
  
  const openDeleteMenu = (msgId, e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    setDeleteMenu({ msgId, x: rect.left, y: rect.top - 10 });
  };
  const startLongPress = (msgId, e) => {
    const touch = e.touches?.[0]; const x = touch?.clientX || 20; const y = touch?.clientY || 100;
    clearTimeout(longPressTimer.current);
    longPressTimer.current = setTimeout(() => setDeleteMenu({msgId,x:Math.max(8,Math.min(x,window.innerWidth-170)),y:Math.max(60,y-70)}), 550);
  };
  const cancelLongPress = () => clearTimeout(longPressTimer.current);
  
  // Filter messages: hide deleted ones
  const visibleMessages = messages.filter(msg => {
    if (msg.deleted_for === 'both') return false;
    if (msg.deleted_for_sender && msg.sender_id === user.id) return false;
    if (msg.deleted_for_receiver && msg.sender_id !== user.id) return false;
    return true;
  });

  return (
    <div className="chat-overlay" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="chat-box">
        {connBanner && <div className={`conn-banner ${connBanner.cls}`}>{connBanner.text}</div>}
        {quotaMsg && <div className="conn-banner banner-offline">{quotaMsg}</div>}
        {retention.effectiveMode === 'none' && <div className="conn-banner banner-offline">🔒 仅当天保留（次日00:00删除）</div>}

        {/* 头部 */}
        <div className="chat-header">
          <div className="chat-partner">
            <div className="chat-partner-avatar">{partner?.display_name?.charAt(0)?.toUpperCase() || '?'}</div>
            <div>
              <div className="chat-partner-name">{partnerFlag} {partner?.display_name || partner?.username}</div>
              <div className="chat-partner-status" style={{ display:'flex', alignItems:'center', gap:8 }}>
                <span className={`status-dot ${connState===CONN_STATE.CONNECTED?'online':'offline'}`} />
                翻译为 {LANG_NAMES[targetLang] || targetLang}
                {membership && <span style={{ fontSize:'0.7rem', opacity:0.6 }}>| {membership.badge} {membership.label}</span>}
              </div>
            </div>
          </div>
          <div className="chat-header-controls">
            <button className="btn-icon" onClick={toggleRetention} disabled={retentionSaving} title="聊天记录设置（适用于所有聊天）" style={{width:'auto',padding:'0 9px',fontSize:'.72rem'}}>
              {retention.ownMode === 'none' ? '🔒 仅当天' : '🕒 保留5天'}
            </button>
            <select className="chat-lang-select" value={targetLang} onChange={e => setTargetLang(e.target.value)}>
              {LANG_LIST.map(l => <option key={l.code} value={l.code}>{l.label}</option>)}
            </select>
            <select className="chat-lang-select" value={sourceLang} onChange={e => setSourceLang(e.target.value)}
              title="我用的语言">
              {LANG_LIST.map(l => <option key={l.code} value={l.code}>{l.label}</option>)}
            </select>
          </div>
          {/* Voice and video call buttons */}
          {session.id && partner && !call?.callState && <>
            <button onClick={() => call?.startCall(partner.user_id, partner.display_name || partner.username, 'audio')} style={{
              position:'fixed', top:12, right:64, zIndex:10001, width:40, height:40, borderRadius:'50%',
              background:'rgba(79,124,255,0.75)', border:'1px solid rgba(255,255,255,0.15)', color:'white', fontSize:'1rem', cursor:'pointer',
              display:'flex', alignItems:'center', justifyContent:'center', backdropFilter:'blur(8px)', WebkitBackdropFilter:'blur(8px)'
            }} title="语音通话">📞</button>
            <button onClick={() => call?.startCall(partner.user_id, partner.display_name || partner.username, 'video')} style={{
              position:'fixed', top:12, right:112, zIndex:10001, width:40, height:40, borderRadius:'50%',
              background:'rgba(139,92,246,0.78)', border:'1px solid rgba(255,255,255,0.15)', color:'white', fontSize:'1rem', cursor:'pointer',
              display:'flex', alignItems:'center', justifyContent:'center', backdropFilter:'blur(8px)', WebkitBackdropFilter:'blur(8px)'
            }} title="视频通话">📹</button>
          </>}
          {/* Floating close button */}
          <button onClick={onClose} style={{
            position:'fixed', top:12, right:12, zIndex:10001,
            width:40, height:40, borderRadius:'50%',
            background:'rgba(0,0,0,0.5)', border:'1px solid rgba(255,255,255,0.15)',
            color:'white', fontSize:'1.2rem', cursor:'pointer',
            display:'flex', alignItems:'center', justifyContent:'center',
            backdropFilter:'blur(8px)', WebkitBackdropFilter:'blur(8px)'
          }} title="关闭聊天">✕</button>
        </div>

        {openingMove && visibleMessages.length===0 && <div style={{margin:'8px 12px 0',padding:'10px 12px',borderRadius:10,background:'rgba(236,72,153,.09)',border:'1px solid rgba(236,72,153,.24)',fontSize:'.8rem'}}><strong>💬 {partner?.display_name||partner?.username} 想聊：</strong><span style={{display:'block',margin:'4px 0 7px'}}>{openingMove}</span><button className="btn btn-secondary" style={{padding:'4px 9px',fontSize:'.72rem'}} onClick={()=>{setInput(openingMove);setTimeout(()=>inputRef.current?.focus(),0)}}>用这个问题开场</button></div>}

        {/* 消息区 */}
        <div className="chat-messages">
          {visibleMessages.length === 0 && (
            <div className="chat-empty">
              <div className="chat-empty-icon">🌍</div>
              <p>{partnerFlag} {partner?.display_name} 在 {partner?.country_code || '远方'}</p>
              <p style={{ fontSize:'0.82rem', opacity:0.7 }}>消息自动翻译成 <strong>{LANG_NAMES[targetLang] || targetLang}</strong></p>
              {membership?.quota?.daily > 0 && (
                <p style={{ fontSize:'0.78rem', marginTop:8, color:'var(--accent2)' }}>
                  💬 今日剩余 {membership.quota.remaining}/{membership.quota.daily} 条免费翻译
                </p>
              )}
              <div style={{ marginTop:10, fontSize:'0.75rem', color:'var(--text-dim)' }}>
                💡 试试: "Nice to meet you" · "你好" · "I love music"
              </div>
            </div>
          )}
          {visibleMessages.map(msg => {
            const isMine = msg.sender_id === user.id;
            const isImage = msg.type === 'image';
            return (
              <div key={msg.id} className={`msg ${isMine ? 'msg-sent' : 'msg-received'} ${msg._pending ? 'msg-pending' : ''}`}
                style={{position:'relative',cursor:'context-menu'}}
                onContextMenu={(e) => { e.preventDefault(); openDeleteMenu(msg.id, e); }}
                onTouchStart={(e) => startLongPress(msg.id, e)} onTouchEnd={cancelLongPress} onTouchMove={cancelLongPress} onTouchCancel={cancelLongPress}>
                {isImage ? (
                  <div style={{maxWidth:240}}>
                    <img src={msg.image_url} alt=""
                      style={{maxWidth:'100%',maxHeight:300,borderRadius:8,display:'block',cursor:'pointer',filter:revealedImages[msg.id]?'none':'blur(18px)',transition:'filter .2s'}}
                      onClick={() => { if (!revealedImages[msg.id]) setRevealedImages(v=>({...v,[msg.id]:true})); else setPreviewImage(msg.image_url); }}
                      title={revealedImages[msg.id] ? '全屏查看' : '点击查看图片'}
                    />
                  </div>
                ) : (
                  <div className="msg-text">{msg.translated_text || msg.original_text}</div>
                )}
                {!isImage && msg.original_text !== msg.translated_text && !msg._pending && (
                  <div className="msg-meta">
                    <span>{msg.original_text}</span> <span style={{opacity:0.5}}>→</span>
                    <span>{FLAGS[msg.source_language]||'🌐'} {FLAGS[msg.target_language]||'🌐'}</span>
                  </div>
                )}
                {msg._pending && <div className="msg-pending-badge">⏳ 待发送</div>}
                {/* Three dots menu trigger */}
                <button
                  className="msg-menu-btn"
                  onClick={(e) => openDeleteMenu(msg.id, e)}
                  style={{
                    position:'absolute', top:2, right: isMine ? 2 : undefined, left: isMine ? undefined : 2,
                    background:'none', border:'none', color:'var(--text-dim)', cursor:'pointer',
                    fontSize:'0.75rem', padding:'2px 6px', borderRadius:4, opacity:0.45,
                    transition:'opacity 0.15s'
                  }}
                  onMouseEnter={e => e.target.style.opacity = 1}
                  onMouseLeave={e => e.target.style.opacity = 0}
                >⋮</button>
              </div>
            );
          })}
          
          {/* Delete menu popup */}
          {deleteMenu && (
            <div
              style={{
                position:'fixed', left:deleteMenu.x, top:deleteMenu.y, zIndex:10000,
                background:'#1a1a2e', border:'1px solid #333', borderRadius:8,
                padding:'4px 0', minWidth:150, boxShadow:'0 4px 20px rgba(0,0,0,0.4)'
              }}
              onClick={() => setDeleteMenu(null)}
            >
              <div
                onClick={(e) => { e.stopPropagation(); handleDelete(deleteMenu.msgId, 'me'); }}
                style={{padding:'8px 14px', cursor:'pointer', fontSize:'0.85rem', color:'white',
                  transition:'background 0.15s'}}
                onMouseEnter={e => e.target.style.background = 'rgba(255,255,255,0.08)'}
                onMouseLeave={e => e.target.style.background = 'transparent'}
              >🗑️ 删除（仅自己可见）</div>
              <div
                onClick={(e) => { e.stopPropagation(); handleDelete(deleteMenu.msgId, 'both'); }}
                style={{padding:'8px 14px', cursor:'pointer', fontSize:'0.85rem', color:'#ff6b6b',
                  transition:'background 0.15s'}}
                onMouseEnter={e => e.target.style.background = 'rgba(255,107,107,0.08)'}
                onMouseLeave={e => e.target.style.background = 'transparent'}
              >🗑️ 删除（双方删除）</div>
            </div>
          )}
          
          {/* Click outside to close menu */}
          {deleteMenu && (
            <div
              style={{position:'fixed', top:0, left:0, right:0, bottom:0, zIndex:9999}}
              onClick={() => setDeleteMenu(null)}
            />
          )}
          

          {partnerTyping && <div className="typing-indicator"><div className="typing-dots"><span/><span/><span/></div>{partner?.display_name} 正在输入...</div>}
          <div ref={messagesEndRef} />
        </div>

        {/* 升级提示条 */}
        {showUpgrade && (
          <div className="upgrade-bar">
            ⭐ 还剩 {translationLeft} 条免费翻译 ·
            <button className="btn-upgrade" onClick={() => navigate('/pricing')}>升级会员 →</button>
          </div>
        )}

        {/* 输入区 */}
        <input ref={fileInputRef} type="file" accept="image/*" style={{display:'none'}}
          onChange={(e) => { const f = e.target.files?.[0]; if (f) sendImage(f); e.target.value = ''; }} />
        <div className="chat-input-area">
          <button className="btn-icon" onClick={handleCameraCapture} title="拍照发送"
            style={{width:36,height:36,borderRadius:'50%',fontSize:'1rem',flexShrink:0,marginRight:4}}>📷</button>
          <button className="btn-icon" onClick={handleImagePick} title="从相册选择"
            style={{width:36,height:36,borderRadius:'50%',fontSize:'1rem',flexShrink:0,marginRight:6}}>🖼️</button>
          <textarea ref={inputRef} value={input} onChange={handleInputChange} onKeyDown={handleKeyDown}
            placeholder={isFree && translationLeft === 0 ? '今日翻译已用完 ⬆️ 升级解锁' : `发给 ${partner?.display_name || partner?.username}...`}
            rows={1} disabled={isFree && translationLeft === 0} />
          <button className="btn-send" onClick={sendMessage}
            disabled={!input.trim() || !session.id || (isFree && translationLeft === 0)}>➤</button>
        </div>
        {cameraOpen && (
          <div style={{position:'fixed',inset:0,zIndex:21000,background:'#000',display:'flex',flexDirection:'column',alignItems:'center',justifyContent:'center'}}>
            <video ref={cameraVideoRef} autoPlay playsInline muted style={{width:'100%',height:'100%',objectFit:'cover'}} />
            <button onClick={closeCamera} style={{position:'fixed',top:14,right:14,zIndex:21002,width:42,height:42,borderRadius:'50%',border:'1px solid rgba(255,255,255,.5)',background:'rgba(0,0,0,.55)',color:'#fff',fontSize:'1.3rem'}}>✕</button>
            <button onClick={takeCameraPhoto} style={{position:'fixed',bottom:28,left:'50%',transform:'translateX(-50%)',zIndex:21002,width:72,height:72,borderRadius:'50%',border:'6px solid #fff',background:'rgba(255,255,255,.35)'}} aria-label="拍照" />
          </div>
        )}
        {previewImage && (
          <div onClick={() => setPreviewImage(null)} style={{position:'fixed',inset:0,zIndex:20000,background:'rgba(0,0,0,.96)',display:'flex',alignItems:'center',justifyContent:'center',padding:12}}>
            <button onClick={() => setPreviewImage(null)} style={{position:'fixed',top:14,right:14,zIndex:20001,width:42,height:42,borderRadius:'50%',border:'1px solid rgba(255,255,255,.35)',background:'rgba(0,0,0,.55)',color:'#fff',fontSize:'1.3rem'}}>✕</button>
            <img src={previewImage} alt="聊天图片预览" onClick={e => e.stopPropagation()} style={{display:'block',maxWidth:'100%',maxHeight:'100%',width:'auto',height:'auto',objectFit:'contain',borderRadius:4}} />
          </div>
        )}
      </div>
    </div>
  );
}
